import {
  ButtonDS,
  DialogModalDS,
  Spacer,
  TextCapitalized,
  NumericInputDS,
} from "@qivia/ui";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { useCallback, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import {
  createSepaDirectDebitAsync,
  selectCompaniesSepaDirectDebitData,
} from "../sepaDirectDebitSlice";

export const CreateSepaDirectDebitModal = (props: {
  visible: boolean;
  onCloseModal: () => void;
  currentSepaDirectDebitCompanyUuid: string;
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { visible, onCloseModal, currentSepaDirectDebitCompanyUuid } = props;
  const [amount, setAmount] = useState<number | "">("");
  const [displayError, setDisplayError] = useState<boolean>(false);
  const sepaDirectDebitList = useAppSelector(
    selectCompaniesSepaDirectDebitData,
  );

  const resetModal = useCallback(() => {
    setDisplayError(false);
    setAmount("");
    onCloseModal();
  }, [onCloseModal]);

  const isFormValid = useMemo(() => {
    return amount !== "" && amount > 1;
  }, [amount]);

  const clickToSubmit = useCallback(() => {
    if (!isFormValid) {
      setDisplayError(true);
      return;
    }

    void dispatch(
      createSepaDirectDebitAsync({
        companyUuid: currentSepaDirectDebitCompanyUuid,
        amount: amount !== "" ? amount : 0,
      }),
    );

    onCloseModal();
  }, [
    amount,
    currentSepaDirectDebitCompanyUuid,
    dispatch,
    isFormValid,
    onCloseModal,
  ]);

  const currentSepaDirectDebit = useMemo(() => {
    return sepaDirectDebitList.find(
      (t) => t.companyUuid === currentSepaDirectDebitCompanyUuid,
    );
  }, [sepaDirectDebitList, currentSepaDirectDebitCompanyUuid]);

  if (!currentSepaDirectDebit) {
    return;
  }

  return (
    <DialogModalDS
      visible={visible}
      onClose={resetModal}
      title={t("createSepaDirectDebit.modal.title")}
      width={40}
      bottomButton={
        <ButtonDS
          text={t(`createSepaDirectDebit.modal.confirm`)}
          format={"fill"}
          buttonType={"danger"}
          disabled={!isFormValid}
          onClick={clickToSubmit}
        />
      }
    >
      <StyledColumn>
        <TextCapitalized>
          {t(`createSepaDirectDebit.modal.subtitle`)}
          {currentSepaDirectDebit.companyName}
        </TextCapitalized>
        <Spacer y={3} />
        <NumericInputDS
          label={t("createSepaDirectDebit.modal.amount")}
          placeholder={t("createSepaDirectDebit.modal.amount.placeholder")}
          update={(value) => setAmount(value !== "" ? value : "")}
          value={amount}
          error={
            displayError && amount === ""
              ? t("createSepaDirectDebit.modal.amount.error")
              : amount && amount < 1
                ? t("createSepaDirectDebit.modal.amountTooLow.error")
                : undefined
          }
          isMandatory
          floatNumber
        />
        <Spacer y={1} />
        {t(`createSepaDirectDebit.modal.warning`)}
        <Spacer y={1} />
      </StyledColumn>
    </DialogModalDS>
  );
};

const StyledColumn = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
`;
